import { useEffect, useMemo, useRef, useState } from "react"
import api from "../Services/api";
import create from 'zustand'

import { useToast } from "../Hooks/toast";
import { useHistory } from "react-router-dom";

const permissionIds = [
  "admin",
  "statementsview",
  "pixview",
  "paypix",
  "receivepix",
  "boletocreateview",
  "payboleto",
  "createboleto",
  "p2pview",
  "transp2p",
  "pagadoriaview",
  "pagodariainclude",
  "pagodariaapprove",
  "payrollview",
  "payrollsend",
  "subaccountview",
  "subaccountcreate",
  "operatorsview",
  "operatorscreate",
  "mycardview",
  "antecipationview",
  "statementsbalanceview"
] as const;

export type PermissionId = typeof permissionIds[number];

export interface useAuthStoreProps {
    token: string
    username: string
    password: string
    accepted_terms: boolean
    phone_number: string
    isAutenticated: boolean,
    expireDate: string
    sessionTimeout: number
    permissions: PermissionId[]
    setUser: (props: { username: string, phone_number: string, password: string }) => void
    setAutenticated: (props: { isAutenticated: boolean, accepted_terms: boolean, token: string, expireDate: string, sessionTimeout: number, permissions: PermissionId[] }) => void
    setAcceptedTerms: (props: { accepted_terms: boolean }) => void
    removeUser: () => void
    setSessionTimeout: (props: { sessionTimeout: number }) => void
}

const sessionKey = '@WalletBC';

const defaultUser = {
    token: '',
    username: '',
    password: '',
    phone_number: '',
    accepted_terms: false,
    isAutenticated: false,
    expireDate: '',
    sessionTimeout: 0,
    permissions: []
}

export const useAuthStore = create<useAuthStoreProps>(setState => {
    let user = { ...defaultUser }

    const userJson = sessionStorage.getItem(sessionKey);

    if(userJson) user = JSON.parse(userJson);

    return {
        ...user,
        setUser({ username = '', phone_number = '', password = '' }) {
            setState(() => ({ username, phone_number, password, isAutenticated: false }));
        },
        setAutenticated({ isAutenticated = false, accepted_terms = false, token = '', expireDate = '', sessionTimeout, permissions }) {
            setState(() => ({ isAutenticated, accepted_terms, token, expireDate, sessionTimeout, permissions }));
        },
        setAcceptedTerms({ accepted_terms = false }) {            
            setState(() => ({ accepted_terms }));
        },
        removeUser() {
            setState(() => ({ ...defaultUser }));
        },
        setSessionTimeout({ sessionTimeout = 0 }) {
            setState(() => ({ sessionTimeout }));
        },
    }
})

useAuthStore.subscribe(({ password, ...state }) => {
    sessionStorage.setItem(sessionKey, JSON.stringify(state));
});

export const useAuth = () => {

    const { setAcceptedTerms, setAutenticated, setUser, removeUser, setSessionTimeout, ...user } = useAuthStore();

    const { addToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    const history = useHistory();

    const timeoutRef = useRef(null);

    const formattedTimestamp = useMemo(() => {
    
        let totalSeconds = user.sessionTimeout;
    
        let hours = Math.floor(totalSeconds / 3600);
        totalSeconds %= 3600;
    
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds % 60);
    
        return [
          hours.toString().padStart(2, '0'),
          minutes.toString().padStart(2, '0'),
          seconds.toString().padStart(2, '0'),
        ].join(':');
      }, [user.sessionTimeout])

    useEffect(() => {
        if(user.sessionTimeout < 1) return clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            setSessionTimeout({ sessionTimeout: user.sessionTimeout - 1 });
        }, 1000);

        return () => clearTimeout(timeoutRef.current);
    }, [user.sessionTimeout])

    const auth = async ({ username, password, captcha = '' }) => {
        setIsLoading(true);
        try {
            const { data } =  await api.post('/auth/', { username, password, captcha }); 

            setUser({
                phone_number: data.phone_number,
                password,
                username,
            });

            return history.push('/auth/verification-code');
        }
        catch(err) {
            addToast({
                title: err.response.data.information,
                description: err.response.data.description,
                type: 'error'
            });
        }
        finally {
            setIsLoading(false);
        }
    }

    const twoFactor = async ({ code = '' }) => {
        setIsLoading(true);

        try {
            const { data } = await api.post('/auth/token', { 
                code,
                username: user.username,
                password: user.password
            });

            const { access_token, accepted_terms, expires_in, permissions } = data;

            const expireDate = new Date(expires_in);
            const currentDate = new Date();

            const diffMs = expireDate.getTime() - currentDate.getTime();
            const diffSeconds = Math.floor(diffMs / 1000);
       
            api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

            setAutenticated({
                accepted_terms,
                token: access_token,
                isAutenticated: true,
                expireDate: expires_in,
                sessionTimeout: diffSeconds,
                permissions
            });

            if(!accepted_terms) return history.push('/auth/accepted-terms');

            return history.push('/');
        }
        catch(err) {
            addToast({
                title: err.response.data.information,
                description: err.response.data.description,
                type: 'error'
            });
        }
        finally {
            setIsLoading(false);
        }
    }

    const acceptedTerms = async ({ accepted = true }) => {
        setIsLoading(true);
        try {
            await api.post('/auth/accept_terms', { accepted });  
            setAcceptedTerms({ accepted_terms: accepted });
        }
        catch(err) {
            addToast({
                title: err.response.data.information,
                description: err.response.data.description,
                type: 'error'
            });
        }
        finally {
            setIsLoading(false);
        }
    }

    const resendCode = async () => {
        await auth({ 
            password: user.password,
            username: user.username
        })
    }

    const logout = () => {
        clearTimeout(timeoutRef.current);
        removeUser();
        return history.push('/auth');
    }

    const refleshToken = async () => {

        addToast({
            title: "Sua sessão foi renovada!",
            description: "Sessão renovada com sucesso.",
            type: "info",
        });
    }

    const hasPermission = (permission: PermissionId) => {

        return user.permissions.includes('admin') || user.permissions.includes(permission);
    }

    return {
        auth,
        twoFactor,
        acceptedTerms,
        resendCode,
        logout,
        refleshToken,
        isLoading,
        formattedTimestamp,
        user,
        hasPermission
    }
}


